/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  FormEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {useSelector} from 'react-redux';
import InputAdornment from '@material-ui/core/InputAdornment';
import debounce from 'lodash.debounce';
import {Moment} from 'moment';
import withSignals from '@ion/applications/src/components/hoc/withSignals';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {getUserTimeZone} from '@ion/core/src/timezone/timezone';
import {
  GenericObject,
  ScheduleConfiguration,
  ScheduleDays,
  ScheduledReport
} from '@ion/core/src/types/entities';
import {formatIfValid, getMaxDate, VALID_MANUAL_DATE_FORMATS} from '@ion/core/src/utils/DateUtil';
import {isValue} from '@ion/core/src/utils/StringUtil';
import {
  isCommaSeparatedEmail,
  isCommaSeparatedSecondaryEmail
} from '@ion/core/src/utils/Validators';
import CheckBox from '@ion/dls/src/components/checkbox/CheckBox';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import Dropdown, {Options} from '@ion/dls/src/components/menus/Dropdown';
import DatePicker from '@ion/dls/src/editors/datetimepicker/DatePicker';
import {ReportDropdownChooser} from '@ion/dls/src/editors/ReportDropdownChooser';
import {
  deserializeScheduledReport,
  IntervalDays,
  IntervalTypes,
  ScheduledReportEditorFields,
  serializeScheduledReport,
  TargetType
} from '@ion/dls/src/editors/ScheduledReportEditorUtils';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import useFormSave from '@ion/dls/src/hooks/useFormSave';
import {useFormState, validate} from '@ion/dls/src/hooks/useFormState';
import {useInitialState} from '@ion/dls/src/hooks/useInitialState';
import getGlobalProp from '@ion/dls/src/props/GlobalPropsConfig';
import Render from '@ion/dls/src/render/Render';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import Divider from '../components/divider/Divider';
import FormGroupFields from '../components/forms/FormGroupFields';
import InfoTooltip from '../components/tooltip/InfoTooltip';
import {toggleThemeStyle} from '../theme/helpers';
import useThemeContext from '../theme/useThemeContext';
import {BodyTypography} from '../typography/BodyTypography';
import TimeInput from './datetimepicker/TimeInput';
import styles from './editorStyles';
import {ScheduleConfigFields, ScheduleFormFields} from './ScheduleEditorUtils';
import SFTPConfigurationChooser from './SFTPConfigurationChooser';
import TimezoneChooser from './TimezoneChooser';
import useSystemTimezone from './useSystemTimezone';

export interface ScheduledReportEditorProps {
  scheduledReport: ScheduledReport;
  selectedDashboardReport: any;
  onSave: (itemData: any) => void;
  submitRef?: MutableRefObject<(event?: any) => void>;
}

const useStyles = (theme: DLSTheme) => ({
  errorMessage : {
    color        : dlsColor('caRed'),
    fontSize     : '12px',
    fontFamily   : 'Roboto',
    fontWeight   : 400,
    marginLeft   : '14px',
    marginRight  : '14px',
    marginTop    : '3px',
    marginBottom : 0
  },
  inlineLabel : {
    color        : dlsColor('grey2'),
    marginBottom : toggleThemeStyle(0, '8px'),
    ...dlsTypography('body3')
  },
  monthFieldLabel : toggleThemeStyle({}, {
    height     : '23px',
    alignItems : 'center'
  }),
  currentInlineLabelError : {
    color : dlsColor('red')
  },
  requiredAsterisk : {
    color         : toggleThemeStyle(`${dlsColor('aaBlue')} !important`, `${dlsColor('red')} !important`),
    margin        : '0 2px',
    fontSize      : '12px',
    lineHeight    : 1,
    verticalAlign : 'middle',
    display       : 'inline-block'
  },
  assetMaintenanceHeader : {
    backgroundColor : dlsColor('white')
  },
  intervalInput : {
    paddingLeft : '12px'
  },
  dashboardMeta : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '24px',
    marginTop  : '12px',
    flexWrap   : 'wrap'
  },
  checkboxMargin : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '24px',
    marginTop  : '30px',
    flexWrap   : 'wrap'
  },
  seasonalCheckboxMargin : {
    display    : 'flex',
    alignItems : 'center',
    marginTop  : '30px',
    flexWrap   : 'wrap'
  },
  dashboardValue : {
    marginBottom : 0
  },
  checkboxGroup : {
    marginTop  : '8px',
    marginLeft : '6px'
  },
  fieldHeader : {
    display    : 'flex',
    alignItems : 'baseline',
    gap        : '12px',
    flexWrap   : 'wrap'
  },
  inlineErrorMessage : {
    color      : dlsColor('caRed'),
    fontSize   : '12px',
    fontFamily : 'Roboto',
    fontWeight : 400
  },
  visibleDivider : {
    backgroundColor : dlsColor('grey3'),
    margin          : '12px 0'
  },
  formWrapper : {
    padding : toggleThemeStyle('0 14px', '0px')
  },
  formSection : {
    padding : '16px 0'
  },
  form : {
    overflowY : toggleThemeStyle('hidden', 'auto')
  },
  scheduleRow : {
    display    : 'flex',
    alignItems : 'flex-start',
    gap        : 24,
    width      : '100%',
    flexWrap   : 'wrap'
  },
  repeatSection : {
    width : '200px'
  },
  seasonalSection : {
    display    : 'flex',
    alignItems : 'center',
    marginTop  : 32
  },
  monthSection : {
    width : 320
  }
});

const reportDropdownChooser = {
  width : '100%'
};

// builds the valid manual date formats from the user's preferred date format, with and without zero-padding ...
const getUserScheduleDateFormats = (): string[] => {
  const dateFormat = UserPreferences.getSystemDateFormat();
  return [dateFormat, dateFormat.replace('MM', 'M').replace('DD', 'D')];
};

const isSFTPConfigurationsV2Enabled = () => getGlobalProp('admin-groups.sftpConfigurations.v2.enabled', false);

/**
 * @ScheduledReportEditor
 * The Asset Form with set fields and encapsulated logic for handling the form's state,
 * including validation.
 *
 * @param {boolean} open Determines if the AssetEditor dialog should be open
 * @param {object} account User's account object
 * @param {Function} onCloseDialog Callback function fired when the dialog requests to be closed
 * @param {Function} onSubmit Function the be run when the form is submitted.
 *   The function is passed an Asset object
 * @param {object} asset Asset that may be passed to the form (like in the case of editing)
 * @param {boolean} clear If true we clear the form state
 * @param {boolean} loading disables any for submission while the form is busy
 */
export const ScheduledReportEditor = ({
  scheduledReport,
  selectedDashboardReport,
  onSave,
  submitRef
}: ScheduledReportEditorProps) => {
  const editorStyles  = useExtendStyles(styles, useStyles);
  const classes       = editorStyles();
  const formRef       = useRef<HTMLFormElement>(null);
  const {globalGroup} = useSelector(({globalGroup}) => globalGroup);
  const accountId     = entityId(globalGroup);
  const configRef     = useRef<HTMLInputElement>(null);

  const [minDate, setMinDate]                     = useState<Moment>(null);
  const [startTimeKey, setStartTimeKey]           = useState(0);
  const [endTimeKey, setEndTimeKey]               = useState(0);
  const [startDateKey, setStartDateKey]           = useState(0);
  const [endDateKey, setEndDateKey]               = useState(0);
  const [isSeasonalSchedule, setSeasonalSchedule] = useState(false);

  const {
    isLegacy  : isLegacyTheme,
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const scheduleDateFormats = isCurrentTheme ? VALID_MANUAL_DATE_FORMATS : getUserScheduleDateFormats();

  // the legacy picker shows string values as-is, so hand it the date in the user's preferred format ...
  const getScheduleDateValue = (value: unknown) => (
    !isCurrentTheme && value instanceof Date ? formatIfValid(value, scheduleDateFormats[0]) : value
  );

  useSystemTimezone();

  const [formState, setFormState] = useFormState(
    useInitialState({
      generator : deserializeScheduledReport,
      params    : scheduledReport
    })
  );

  const scheduleType        = formState[ScheduledReportEditorFields.ScheduleType]?.value || IntervalTypes.once;
  const isWeeklySchedule    = scheduleType === IntervalTypes.week;
  const isMonthlySchedule   = scheduleType === IntervalTypes.month;
  const isRecurringSchedule = scheduleType !== IntervalTypes.once;
  const isHourlySchedule    = scheduleType === IntervalTypes.hour;
  const isDailySchedule     = scheduleType === IntervalTypes.day;

  const scheduledReportDayOptions = useMemo<Options[]>(() => (
    translate('common:editors.dateTime.scheduledReportDaysOptions', {returnObjects: true}) as Options[]
  ), []);

  const scheduledReportMonthOptions = useMemo<Options[]>(() => (
    translate('common:editors.dateTime.months', {returnObjects: true}) as Options[]
  ), []);

  const scheduleTypeOptions = useMemo<Options[]>(() => (
    translate('sisense:addScheduleReportInterval', {returnObjects: true}) as Options[]
  ), []);

  const selectedDaysOfWeek = useMemo(() => {
    const dayValues = formState[ScheduledReportEditorFields.DaysOfWeek]?.value || [];
    return scheduledReportDayOptions.filter(({value}) => dayValues.includes(value));
  }, [formState, scheduledReportDayOptions]);

  const selectedSeasonalMonths = useMemo(() => {
    const monthValues = formState[ScheduledReportEditorFields.SeasonalMonths]?.value || [];
    return scheduledReportMonthOptions.filter(({id}) => monthValues.includes(id));
  }, [formState, scheduledReportMonthOptions]);

  // defines the route translation interpolation options ...
  const translations = {entityLabel: translate('common:entityLabels.schedules')};

  // initialize the schedule configurations (add an ID to each, and sort the list by day) ...
  function initializeConfigsState(configs: ScheduleConfiguration[]): GenericObject[] {
    if (configs) {
      const configsInit = configs.map((config, i) => ({...config, id: i}));
      const dayMap = {
        [ScheduleDays.Sunday]    : 1,
        [ScheduleDays.Monday]    : 2,
        [ScheduleDays.Tuesday]   : 3,
        [ScheduleDays.Wednesday] : 4,
        [ScheduleDays.Thursday]  : 5,
        [ScheduleDays.Friday]    : 6,
        [ScheduleDays.Saturday]  : 7
      };
      return configsInit.sort((a, b) =>
        (a[ScheduleConfigFields.Day] ? dayMap[a[ScheduleConfigFields.Day]] : 0) -
        (b[ScheduleConfigFields.Day] ? dayMap[b[ScheduleConfigFields.Day]] : 0));
    }
    return configs;
  };

  const configStateRef = useRef(initializeConfigsState(formState[ScheduleFormFields.Configs]?.value));

  // debounces textfield changes so as to only update state once the change has been completed ...
  const debounceFieldChange = debounce((target: TargetType) => {
    const {name, value, required = false} = target;
    let error = false;
    if (name === ScheduledReportEditorFields.Emails) {
      error = !validate(value, isCommaSeparatedSecondaryEmail);
    } else if (name === ScheduledReportEditorFields.ErrorEmails) {
      error = !validate(value, isCommaSeparatedSecondaryEmail);
    }
    setFormState({[name]: {name, value, required, error}});
  }, 400);

  function handleEmailChange(e: ChangeEvent<HTMLInputElement>) {
    e?.persist();
    const {name, required, value} = e?.target;
    setFormState({[name]: {name, value, required, error: false}});
    debounceFieldChange(e?.target);
  };

  // invoked when a textfield change is made, delegating the debounce change handler ...
  function onFieldChange(e: ChangeEvent<HTMLInputElement>) {
    e.persist();
    debounceFieldChange(e.target);
  };

  // clears the state of the form and all related fields ...
  function clearForm() {
    setFormState({}, true);
  };

  function onCheckBoxChange({target}: ChangeEvent<HTMLInputElement>) {
    const {name, checked, required = false} = target;
    setFormState({[name]: {name, value: checked, required}});
  };

  function getReportTypeValue(nextValues?: Partial<Record<ScheduledReportEditorFields, boolean>>) {
    const pdf = nextValues?.[ScheduledReportEditorFields.ReportTypePdf] ??
      !!formState[ScheduledReportEditorFields.ReportTypePdf]?.value;
    const csv = nextValues?.[ScheduledReportEditorFields.ReportTypeCsv] ??
      !!formState[ScheduledReportEditorFields.ReportTypeCsv]?.value;
    const xlsCombined = nextValues?.[ScheduledReportEditorFields.ReportTypeXlsCombined] ??
      !!formState[ScheduledReportEditorFields.ReportTypeXlsCombined]?.value;
    return {pdf, csv, xlsCombined};
  }

  function handleCheckBoxChange({target: {name, checked, required = false}}: ChangeEvent<HTMLInputElement>) {
    const isReportTypeField = [
      ScheduledReportEditorFields.ReportTypePdf,
      ScheduledReportEditorFields.ReportTypeCsv,
      ScheduledReportEditorFields.ReportTypeXlsCombined
    ].includes(name as ScheduledReportEditorFields);
    if (isReportTypeField) {
      const reportType = getReportTypeValue({[name]: checked});
      const hasSelectedReportType = Object.values(reportType).some(Boolean);
      setFormState({
        [name]                                   : {name, value: checked, required},
        [ScheduledReportEditorFields.ReportType] : {
          name     : ScheduledReportEditorFields.ReportType,
          value    : reportType,
          required : true,
          error    : !hasSelectedReportType
        }
      });
      return;
    }
    if (name === ScheduledReportEditorFields.CustomizeEmailMessage) {
      setFormState({
        [name]                                     : {name, value: checked, required},
        [ScheduledReportEditorFields.EmailSubject] : {
          ...formState[ScheduledReportEditorFields.EmailSubject],
          name     : ScheduledReportEditorFields.EmailSubject,
          required : checked,
          error    : false
        }
      });
      return;
    }
    if (name === ScheduledReportEditorFields.SeasonalSchedule) {
      setSeasonalSchedule(checked);
      setFormState({
        [name]                                       : {name, value: checked, required},
        [ScheduledReportEditorFields.SeasonalMonths] : {
          ...formState[ScheduledReportEditorFields.SeasonalMonths],
          name     : ScheduledReportEditorFields.SeasonalMonths,
          required : checked,
          error    : false
        }
      });
      return;
    }
    setFormState({[name]: {name, value: checked, required}});
  }

  // invoked when a stand-alone, chooser specific component change is made ...
  function onChooserFieldChange({name, value, required = false}: TargetType) {
    setFormState({[name]: {name, value, required}});
  };

  function handleDateChange({name, value, required = false}) {
    setFormState({[name]: {name, value, required}});
  };

  function handleFieldChange({target, persist}: ChangeEvent<HTMLInputElement>) {
    persist();
    const {name, value, required = false} = target;
    setFormState({[name]: {name, value, required}});
  };

  //return true if the day interval is selected
  const isDailyInterval = (day: string) => {
    return IntervalTypes.once === day;
  };

  function validateStartTime(value) {
    let valid = true;
    let errorText = '';
    const scheduleType = formState[ScheduledReportEditorFields.ScheduleType]?.value;
    const endTime = formState[ScheduledReportEditorFields.EndDate]?.value;
    if (isDailyInterval(scheduleType)) {
      return {valid, errorText};
    }
    if (value && endTime && (value >= endTime)) {
      valid = false;
      errorText = translate('reports:date.startDate.error');
    } else if (!value && endTime) {
      valid = false;
      errorText = translate('common:editors.shared.required');
    }
    return {valid, errorText};
  };

  // invoked when a stand-alone, chooser-specific component change is made ...
  function onDayChange({name, value, required = true}: TargetType) {
    const updated = {...configStateRef.current, [name]: value};
    setFormState({[name]: {name, value, required}});
    configStateRef.current = updated;
    setConfigs(updated);
  };

  // invoked when a schedule configuration change is made ...
  function setConfigs(configs: GenericObject[]): void {
    setFormState({
      [ScheduleFormFields.Configs] : {
        name     : ScheduleFormFields.Configs,
        value    : configs,
        required : true
      }
    });
  };

  function handleChooserChange({name, value, required = false, error = false}) {
    setFormState({[name]: {name, value: isValue(value, 'none') ? null : value, required}});
  };

  const getRequiredLabel = useCallback((label: string, hasError = false) => (
    <BodyTypography className={isCurrentTheme && hasError
      ? `${classes.inlineLabel} ${classes.currentInlineLabelError}`
      : classes.inlineLabel}>
      {label}
      <span className={classes.requiredAsterisk}>*</span>
    </BodyTypography>
  ), [classes.inlineLabel, classes.currentInlineLabelError, classes.requiredAsterisk, isCurrentTheme]);

  const getFormState = useCallback((state) => ({
    ...state,
    [ScheduledReportEditorFields.ReportType] : {
      ...state[ScheduledReportEditorFields.ReportType],
      value : {
        pdf         : !!state[ScheduledReportEditorFields.ReportTypePdf]?.value,
        csv         : !!state[ScheduledReportEditorFields.ReportTypeCsv]?.value,
        xlsCombined : !!state[ScheduledReportEditorFields.ReportTypeXlsCombined]?.value
      },
      required : true,
      error    : ![
        state[ScheduledReportEditorFields.ReportTypePdf]?.value,
        state[ScheduledReportEditorFields.ReportTypeCsv]?.value,
        state[ScheduledReportEditorFields.ReportTypeXlsCombined]?.value
      ].some(Boolean)
    },
    [ScheduledReportEditorFields.ScheduleType] : {
      ...state[ScheduledReportEditorFields.ScheduleType],
      required : true
    },
    [ScheduledReportEditorFields.ErrorEmails] : {
      ...state[ScheduledReportEditorFields.ErrorEmails],
      required : false
    },
    [ScheduledReportEditorFields.EmailSubject] : {
      ...state[ScheduledReportEditorFields.EmailSubject],
      required : !!state[ScheduledReportEditorFields.CustomizeEmailMessage]?.value,
      error    : !!state[ScheduledReportEditorFields.CustomizeEmailMessage]?.value &&
        !!state[ScheduledReportEditorFields.EmailSubject]?.error
    },
    [ScheduledReportEditorFields.Timezone] : {
      ...state[ScheduledReportEditorFields.Timezone],
      required : true
    },
    [ScheduledReportEditorFields.StartTime] : {
      ...state[ScheduledReportEditorFields.StartTime],
      required : true
    },
    [ScheduledReportEditorFields.EndTime] : {
      ...state[ScheduledReportEditorFields.EndTime],
      required : isHourlySchedule
    },
    [ScheduledReportEditorFields.StartDate] : {
      ...state[ScheduledReportEditorFields.StartDate],
      required : true
    },
    [ScheduledReportEditorFields.RepeatEveryDay] : {
      ...state[ScheduledReportEditorFields.RepeatEveryDay],
      required : isRecurringSchedule && !isWeeklySchedule
    },
    [ScheduledReportEditorFields.MonthDays] : {
      ...state[ScheduledReportEditorFields.MonthDays],
      required : isMonthlySchedule
    },
    [ScheduledReportEditorFields.DaysOfWeek] : {
      ...state[ScheduledReportEditorFields.DaysOfWeek],
      required : isWeeklySchedule
    },
    [ScheduledReportEditorFields.EndDate] : {
      ...state[ScheduledReportEditorFields.EndDate],
      required : false
    },
    [ScheduledReportEditorFields.SftpServer] : {
      ...state[ScheduledReportEditorFields.SftpServer],
      value    : state[ScheduledReportEditorFields.SftpServer]?.value || {},
      required : !(state[ScheduledReportEditorFields.Emails]?.value)
    },
    [ScheduledReportEditorFields.Directory] : {
      ...state[ScheduledReportEditorFields.Directory],
      required : !!state[ScheduledReportEditorFields.SftpServer]?.value
    },
    [ScheduledReportEditorFields.Emails] : {
      ...state[ScheduledReportEditorFields.Emails],
      required : !(state[ScheduledReportEditorFields.SftpServer]?.value)
    },
    [ScheduledReportEditorFields.SeasonalMonths] : {
      ...state[ScheduledReportEditorFields.SeasonalMonths],
      required : state[ScheduledReportEditorFields.SeasonalSchedule]?.value
    }
  }), [isMonthlySchedule, isRecurringSchedule, isWeeklySchedule, isHourlySchedule, isDailySchedule]);

  const onSubmit = useFormSave({
    serialize : (state) => serializeScheduledReport(state, selectedDashboardReport),
    formState,
    getFormState,
    setFormState,
    validate  : {
      [ScheduledReportEditorFields.Emails]      : ({value}) => value && validate(value, isCommaSeparatedEmail),
      [ScheduledReportEditorFields.ErrorEmails] : ({value}) => (
        validate(value, isCommaSeparatedSecondaryEmail)
      )
    },
    onSave
  });

  const scrollToFirstError = useCallback(() => {
    const invalidElements = Array.from(formRef.current?.querySelectorAll(
      'input[aria-invalid="true"], textarea[aria-invalid="true"], [role="button"][aria-invalid="true"], .Mui-error input, .Mui-error textarea'
    ) || []) as HTMLElement[];
    const invalidElement = invalidElements.find((element) => (
      !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length)
    ));

    if (invalidElement) {
      invalidElement.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'nearest'});
      invalidElement.focus?.();
    }
  }, []);

  const handleSubmit = useCallback((e?: FormEvent<HTMLFormElement>) => {
    onSubmit(e);
    requestAnimationFrame(scrollToFirstError);
  }, [onSubmit, scrollToFirstError]);

  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
  }, [handleSubmit, submitRef]);

  const disableSeasonal = scheduleType === IntervalTypes.once;

  useEffect(() => {
    if (disableSeasonal) {
      setSeasonalSchedule(false);
      setFormState({
        [ScheduledReportEditorFields.SeasonalMonths] : {
          name     : ScheduledReportEditorFields.SeasonalMonths,
          value    : [],
          error    : false,
          required : false
        }
      });
    } else {
      if (formState[ScheduledReportEditorFields.Schedule]?.value?.months) {
        setSeasonalSchedule(true);
      }
    }
  }, [disableSeasonal]);

  function handleSFTPConnectionChange(selected) {
    const sftpValue = selected !== 'None' ? selected : {};
    setFormState({
      [ScheduledReportEditorFields.SftpServer] : {
        name  : ScheduledReportEditorFields.SftpServer,
        value : sftpValue
      }
    });
  }

  return (
    <form ref={formRef} className={classes.form} onSubmit={handleSubmit}>
      <div className={classes.formWrapper}>
        <FormSection title={translate('common:editors.shared.details', translations)}
          legend={translate('common:editors.shared.fieldsRequired')}
          overrideClasses={classes}>
          <>
            <FormGroupFields>
              <FormItem fullWidth>
                <FormTextField name={ScheduledReportEditorFields.Name}
                  fieldLabel={translate('common:editors.labels.name')}
                  placeholder={translate('common:editors.name.placeholder')}
                  errorText={translate('common:editors.shared.required')}
                  defaultValue={formState[ScheduledReportEditorFields.Name]?.value}
                  hasError={formState[ScheduledReportEditorFields.Name]?.error}
                  onChange={onFieldChange}
                  required />
              </FormItem>
              <FormItem fullWidth>
                <BodyTypography className={classes.inlineLabel}>
                  {translate('sisense:formLabels.dashboardName')}
                </BodyTypography>
                <div className={classes.dashboardMeta}>
                  <BodyTypography className={classes.dashboardValue}>
                    {selectedDashboardReport?.title}
                  </BodyTypography>
                  <CheckBox label={translate('sisense:formLabels.customFilters')}
                    name={ScheduledReportEditorFields.CustomFiltersEnabled}
                    onChange={onCheckBoxChange}
                    checked={formState[ScheduledReportEditorFields.CustomFiltersEnabled]?.value} />
                </div>
              </FormItem>
            </FormGroupFields>
            <Render.If condition={isLegacyTheme}>
              <Divider className={classes.visibleDivider} />
            </Render.If>
            <FormGroupFields>
              <FormItem fullWidth className={classes.checkboxTextFont}>
                <>
                  <div className={classes.fieldHeader}>
                    {getRequiredLabel(
                      translate('sisense:formLabels.fileFormats'),
                      formState[ScheduledReportEditorFields.ReportType]?.error
                    )}
                    <Render.If condition={formState[ScheduledReportEditorFields.ReportType]?.error}>
                      <span className={classes.inlineErrorMessage}>
                        {translate('common:editors.shared.required')}
                      </span>
                    </Render.If>
                  </div>
                  <div className={classes.checkboxGroup}>
                    <CheckBox label={translate('sisense:formLabels.csvLabel')}
                      name={ScheduledReportEditorFields.ReportTypeCsv}
                      onChange={handleCheckBoxChange}
                      checked={formState[ScheduledReportEditorFields.ReportTypeCsv]?.value} />
                    <CheckBox label={translate('sisense:formLabels.xlsLabel')}
                      name={ScheduledReportEditorFields.ReportTypeXlsCombined}
                      onChange={handleCheckBoxChange}
                      checked={formState[ScheduledReportEditorFields.ReportTypeXlsCombined]?.value} />
                    <CheckBox label={translate('sisense:formLabels.pdfLabel')}
                      name={ScheduledReportEditorFields.ReportTypePdf}
                      onChange={handleCheckBoxChange}
                      checked={formState[ScheduledReportEditorFields.ReportTypePdf]?.value} />
                  </div>
                </>
              </FormItem>
              <FormItem halfWidth>
                <SFTPConfigurationChooser accountId={accountId}
                  onChange={handleSFTPConnectionChange}
                  isView={false}
                  isCtaLink={false}
                  customLabel
                  v2={isSFTPConfigurationsV2Enabled()}
                  account={globalGroup}
                  defaultValue={formState[ScheduledReportEditorFields.SftpServer]?.value} />
              </FormItem>
              <FormItem halfWidth>
                <div className={classes.checkboxMargin}>
                  <CheckBox label={translate('sisense:formLabels.overwriteExisting')}
                    name={ScheduledReportEditorFields.OverwriteExisting}
                    onChange={handleCheckBoxChange}
                    checked={formState[ScheduledReportEditorFields.OverwriteExisting]?.value} />
                </div>
              </FormItem>
              <FormItem halfWidth>
                <FormTextField name={ScheduledReportEditorFields.Directory}
                  fieldLabel={translate('sisense:formLabels.archivePath')}
                  placeholder={translate('sisense:formLabels.archivePathPlaceholder')}
                  defaultValue={formState[ScheduledReportEditorFields.Directory]?.value}
                  hasError={formState[ScheduledReportEditorFields.Directory]?.error}
                  errorText={translate('common:editors.shared.required')}
                  onChange={onFieldChange}
                  maxLength={255} />
              </FormItem>
            </FormGroupFields>
            <Render.If condition={isLegacyTheme}>
              <Divider className={classes.visibleDivider} />
            </Render.If>
          </>
        </FormSection>
        <FormSection title={translate('sisense:formLabels.recipients')}
          overrideClasses={classes}>
          <>
            <FormGroupFields>
              <FormItem>
                <FormTextField disabled={false}
                  fieldLabel={translate('common:editors.emailAddress.label')}
                  placeholder={translate('common:editors.emailAddress.placeholder')}
                  helperText={translate('sisense:formLabels.multipleRecipientsHelper')}
                  name={ScheduledReportEditorFields.Emails}
                  onChange={handleEmailChange}
                  hasError={formState[ScheduledReportEditorFields.Emails]?.error}
                  errorText={translate('common:editors.emailAddress.error')}
                  value={formState[ScheduledReportEditorFields.Emails]?.value} />
              </FormItem>
              <FormItem>
                <FormTextField disabled={false}
                  fieldLabel={translate('sisense:formLabels.errorEmail')}
                  placeholder={translate('common:editors.emailAddress.placeholder')}
                  helperText={translate('sisense:formLabels.multipleRecipientsHelper')}
                  name={ScheduledReportEditorFields.ErrorEmails}
                  onChange={handleEmailChange}
                  hasError={formState[ScheduledReportEditorFields.ErrorEmails]?.error}
                  errorText={translate('common:editors.emailAddress.error')}
                  value={formState[ScheduledReportEditorFields.ErrorEmails]?.value} />
              </FormItem>
            </FormGroupFields>
            <Render.If condition={isLegacyTheme}>
              <Divider className={classes.visibleDivider} />
            </Render.If>
          </>
        </FormSection>
        <FormSection title={translate('sisense:formLabels.deliveryOptions')}
          overrideClasses={classes}>
          <>
            <FormGroupFields>
              <FormItem fullWidth>
                <CheckBox label={translate('sisense:formLabels.customizeEmail')}
                  name={ScheduledReportEditorFields.CustomizeEmailMessage}
                  onChange={handleCheckBoxChange}
                  checked={formState[ScheduledReportEditorFields.CustomizeEmailMessage]?.value} />
              </FormItem>
              <Render.If condition={formState[ScheduledReportEditorFields.CustomizeEmailMessage]?.value}>
                <>
                  <FormItem fullWidth>
                    <FormTextField name={ScheduledReportEditorFields.EmailSubject}
                      fieldLabel={translate('sisense:formLabels.subjectLabel')}
                      placeholder={translate('sisense:formLabels.subjectLabel')}
                      errorText={translate('common:editors.shared.required')}
                      defaultValue={formState[ScheduledReportEditorFields.EmailSubject]?.value}
                      hasError={formState[ScheduledReportEditorFields.EmailSubject]?.error}
                      onChange={onFieldChange}
                      required />
                  </FormItem>
                  <FormItem fullWidth>
                    <FormTextField name={ScheduledReportEditorFields.EmailBody}
                      fieldLabel={translate('sisense:formLabels.bodyLabel')}
                      placeholder={translate('sisense:formLabels.bodyPlaceholder')}
                      errorText={translate('common:editors.shared.required')}
                      defaultValue={formState[ScheduledReportEditorFields.EmailBody]?.value}
                      hasError={formState[ScheduledReportEditorFields.EmailBody]?.error}
                      onChange={onFieldChange}
                      maxLength={1024}
                      minRows={6}
                      maxRows={6}
                      multiline />
                  </FormItem></>
              </Render.If>
              <FormItem halfWidth>
                <FormTextField name={ScheduledReportEditorFields.FileNameTemplate}
                  fieldLabel={translate('sisense:formLabels.filenameLabel')}
                  placeholder={translate('sisense:formLabels.filenameLabel')}
                  defaultValue={formState[ScheduledReportEditorFields.FileNameTemplate]?.value}
                  hasError={formState[ScheduledReportEditorFields.FileNameTemplate]?.error}
                  onChange={onFieldChange}
                  maxLength={255} />
              </FormItem>
              <FormItem halfWidth>
                <div className={classes.checkboxMargin}>
                  <CheckBox label={translate('sisense:formLabels.compressedText')}
                    name={ScheduledReportEditorFields.Compressed}
                    onChange={handleCheckBoxChange}
                    checked={formState[ScheduledReportEditorFields.Compressed]?.value} />
                </div>
              </FormItem>
            </FormGroupFields>
            <Render.If condition={isLegacyTheme}>
              <Divider className={classes.visibleDivider} />
            </Render.If>
          </>
        </FormSection>
        <FormSection title={translate('common:editors.schedule.label')}
          legend={translate('common:editors.shared.fieldsRequired')}
          overrideClasses={classes}>
          <>
            <FormGroupFields>
              <FormItem fullWidth>
                <div className={classes.scheduleRow}>
                  <div className={classes.repeatSection}>
                    {getRequiredLabel(
                      translate('sisense:formLabels.scheduleTypeLabel'),
                      formState[ScheduledReportEditorFields.ScheduleType]?.error
                    )}
                    <ReportDropdownChooser options={scheduleTypeOptions}
                      style={reportDropdownChooser}
                      required
                      hasError={formState[ScheduledReportEditorFields.ScheduleType]?.error}
                      onChange={function(value) {
                        const selectedOption = scheduleTypeOptions.find((option) => (
                          option.value === value || option.label === value
                        ));
                        const normalizedValue = (selectedOption?.value || value)?.toLowerCase();
                        onChooserFieldChange({
                          name     : ScheduledReportEditorFields.ScheduleType,
                          value    : normalizedValue,
                          required : true
                        });
                        setFormState({
                          [ScheduledReportEditorFields.RepeatEveryDay] : {
                            ...formState[ScheduledReportEditorFields.RepeatEveryDay],
                            name     : ScheduledReportEditorFields.RepeatEveryDay,
                            value    : normalizedValue === IntervalTypes.once ? null : '',
                            required : normalizedValue !== IntervalTypes.once,
                            error    : false
                          },
                          [ScheduledReportEditorFields.MonthDays] : {
                            ...formState[ScheduledReportEditorFields.MonthDays],
                            name     : ScheduledReportEditorFields.MonthDays,
                            value    : normalizedValue === IntervalTypes.month ? '' : null,
                            required : normalizedValue === IntervalTypes.month,
                            error    : false
                          },
                          [ScheduledReportEditorFields.DaysOfWeek] : {
                            ...formState[ScheduledReportEditorFields.DaysOfWeek],
                            name     : ScheduledReportEditorFields.DaysOfWeek,
                            value    : [],
                            required : normalizedValue === IntervalTypes.week,
                            error    : false
                          },
                          [ScheduledReportEditorFields.SeasonalMonths] : {
                            ...formState[ScheduledReportEditorFields.SeasonalMonths],
                            name     : ScheduledReportEditorFields.SeasonalMonths,
                            value    : [],
                            required : false,
                            error    : false
                          },
                          [ScheduledReportEditorFields.SeasonalSchedule] : {
                            ...formState[ScheduledReportEditorFields.SeasonalSchedule],
                            name     : ScheduledReportEditorFields.SeasonalSchedule,
                            value    : false,
                            required : false,
                            error    : false
                          },
                          [ScheduledReportEditorFields.StartTime] : {
                            ...formState[ScheduledReportEditorFields.StartTime],
                            name     : ScheduledReportEditorFields.StartTime,
                            value    : '12:00',
                            required : true,
                            error    : false
                          },
                          [ScheduledReportEditorFields.EndTime] : {
                            ...formState[ScheduledReportEditorFields.EndTime],
                            name     : ScheduledReportEditorFields.EndTime,
                            value    : '12:00',
                            required : true,
                            error    : false
                          },
                          [ScheduledReportEditorFields.StartDate] : {
                            ...formState[ScheduledReportEditorFields.StartDate],
                            name     : ScheduledReportEditorFields.StartDate,
                            value    : null,
                            required : true,
                            error    : false
                          },
                          [ScheduledReportEditorFields.EndDate] : {
                            ...formState[ScheduledReportEditorFields.EndDate],
                            name     : ScheduledReportEditorFields.EndDate,
                            value    : null,
                            required : false,
                            error    : false
                          },
                          [ScheduledReportEditorFields.Timezone] : {
                            ...formState[ScheduledReportEditorFields.Timezone],
                            name     : ScheduledReportEditorFields.Timezone,
                            value    : null,
                            required : true,
                            error    : false
                          }
                        });
                        setSeasonalSchedule(false);
                        handleDateChange({
                          name     : ScheduledReportEditorFields.StartTime,
                          value    : '12:00',
                          required : true
                        });
                        handleDateChange({
                          name     : ScheduledReportEditorFields.StartDate,
                          value    : null,
                          required : true
                        });
                        handleDateChange({
                          name     : ScheduledReportEditorFields.EndTime,
                          value    : '12:00',
                          required : isHourlySchedule
                        });
                        handleDateChange({
                          name     : ScheduledReportEditorFields.EndDate,
                          value    : null,
                          required : false
                        });
                        const endDate = getMaxDate(Number(IntervalDays[value]));
                        setMinDate(endDate);
                        setStartTimeKey(prev => prev + 1);
                        isHourlySchedule ?? setEndTimeKey(prev => prev + 1);
                        setStartDateKey(prev => prev + 1);
                        setEndDateKey(prev => prev + 1);
                      }}
                      defaultValue={formState[ScheduledReportEditorFields.ScheduleType]?.value ||
                          IntervalTypes.once}/>
                  </div>
                  <div className={classes.seasonalCheckboxMargin}>
                    <CheckBox label={translate('sisense:formLabels.seasonalScheduleCheckbox')}
                      disabled={disableSeasonal}
                      name={ScheduledReportEditorFields.SeasonalSchedule}
                      onChange={handleCheckBoxChange}
                      addRightMargin={false}
                      checked={formState[ScheduledReportEditorFields.SeasonalSchedule]?.value} />
                    <InfoTooltip message={translate('common:editors.seasonalSelectionTooltip')} />
                  </div>
                  <Render.If condition={isSeasonalSchedule}>
                    <div className={classes.monthSection} ref={configRef}>
                      <Dropdown required
                        autoComplete
                        multiSelect
                        disableCloseOnSelect
                        options={scheduledReportMonthOptions}
                        fieldLabel={translate('sisense:formLabels.seasonalMonthsDropdown')}
                        fieldTooltip={<InfoTooltip message={translate('common:editors.seasonalMonthsTooltip')} />}
                        placeholder={translate('common:editors.selectMonths')}
                        externalClasses={{fieldLabel: classes.monthFieldLabel}}
                        hasError={formState[ScheduledReportEditorFields.SeasonalMonths]?.error}
                        errorText={translate('common:editors.shared.required')}
                        value={selectedSeasonalMonths}
                        labelAsValue={false}
                        onChange={function(_e, selectedOptions: Options[] = []) {
                          onChooserFieldChange({
                            name  : ScheduledReportEditorFields.SeasonalMonths,
                            value : selectedOptions.map(({id}) => id)
                          });
                        }} />
                    </div>
                  </Render.If>
                </div>
              </FormItem>
              <Render.If condition={isWeeklySchedule}>
                <FormItem halfWidth>
                  <div ref={configRef}>
                    <Dropdown required
                      autoComplete
                      multiSelect
                      disableCloseOnSelect
                      options={scheduledReportDayOptions}
                      fieldLabel={translate('sisense:reportInterval.repeatEvery')}
                      placeholder={translate('common:editors.day.placeholder')}
                      hasError={formState[ScheduledReportEditorFields.DaysOfWeek]?.error}
                      errorText={translate('common:editors.shared.required')}
                      value={selectedDaysOfWeek}
                      labelAsValue={false}
                      onChange={function(_e, selectedOptions: Options[] = []) {
                        onChooserFieldChange({
                          name  : ScheduledReportEditorFields.DaysOfWeek,
                          value : selectedOptions.map(({value}) => value)
                        });
                      }} />
                  </div>
                </FormItem>
              </Render.If>
              <Render.If condition={isRecurringSchedule && !isWeeklySchedule}>
                <FormItem halfWidth>
                  <div>
                    {getRequiredLabel(
                      translate('sisense:reportInterval.repeatInterval'),
                      formState[ScheduledReportEditorFields.RepeatEveryDay]?.error
                    )}
                    <FormTextField required
                      disabled={false}
                      name={ScheduledReportEditorFields.RepeatEveryDay}
                      maxLength={2}
                      InputProps={{
                        ...(isLegacyTheme && {
                          classes : {
                            input : classes.intervalInput
                          }
                        }),
                        endAdornment : (
                          <InputAdornment position='end'>
                            {translate(`sisense:reportInterval.placeHolder.${
                              isMonthlySchedule ? 'month' : isWeeklySchedule ? 'week' : isHourlySchedule ? 'hour' : 'daily'
                            }`)}
                          </InputAdornment>
                        )
                      }}
                      hasError={formState[ScheduledReportEditorFields.RepeatEveryDay]?.error}
                      errorText={translate('common:editors.shared.required')}
                      value={formState[ScheduledReportEditorFields.RepeatEveryDay]?.value}
                      onChange={handleFieldChange}
                      defaultValue={formState[ScheduledReportEditorFields.RepeatEveryDay]?.value}/>
                  </div>
                </FormItem>
              </Render.If>
              <Render.If condition={isMonthlySchedule}>
                <FormItem halfWidth>
                  <div>
                    {getRequiredLabel(
                      translate('sisense:formLabels.monthDays'),
                      formState[ScheduledReportEditorFields.MonthDays]?.error
                    )}
                    <FormTextField required
                      disabled={false}
                      name={ScheduledReportEditorFields.MonthDays}
                      maxLength={100}
                      hasError={formState[ScheduledReportEditorFields.MonthDays]?.error}
                      errorText={translate('common:editors.shared.required')}
                      value={formState[ScheduledReportEditorFields.MonthDays]?.value}
                      onChange={handleFieldChange}
                      defaultValue={formState[ScheduledReportEditorFields.MonthDays]?.value}/>
                  </div>
                </FormItem>
              </Render.If>
              <FormItem>
                <TimezoneChooser name={ScheduledReportEditorFields.Timezone}
                  required={true}
                  includeNone={false}
                  noneOptionLabel={translate('timezones:fields.default')}
                  onChange={handleChooserChange}
                  hasError={formState[ScheduledReportEditorFields.Timezone]?.error}
                  errorText={translate('common:editors.shared.required')}
                  defaultValue={formState[ScheduledReportEditorFields.Timezone]?.value || getUserTimeZone()} />
              </FormItem>
              <FormItem halfWidth>
                <TimeInput value={formState[ScheduledReportEditorFields.StartTime]?.value}
                  hasError={formState[ScheduledReportEditorFields.StartTime]?.error}
                  errorText={translate('common:editors.shared.required')}
                  fieldLabel={translate('sisense:formLabels.startTime')}
                  required
                  onChange={function(value) {
                    handleDateChange({
                      name     : ScheduledReportEditorFields.StartTime,
                      value,
                      required : true
                    });
                  }} />
              </FormItem>
              <Render.If condition={isHourlySchedule}>
                <FormItem halfWidth>
                  <TimeInput value={formState[ScheduledReportEditorFields.EndTime]?.value}
                    hasError={formState[ScheduledReportEditorFields.EndTime]?.error}
                    errorText={translate('common:editors.shared.required')}
                    fieldLabel={translate('sisense:formLabels.endTime')}
                    required
                    onChange={function(value) {
                      handleDateChange({
                        name     : ScheduledReportEditorFields.EndTime,
                        value,
                        required : true
                      });
                    }} />
                </FormItem>
              </Render.If>
              <FormItem halfWidth>
                <DatePicker key = {startDateKey}
                  useDatePickerTextField
                  manualInput
                  isDatePicker
                  validDateTimeFormats={scheduleDateFormats}
                  required
                  allowManualEntry
                  overridePopupStyle={{position: 'relative'}}
                  fieldLabel={translate('sisense:formLabels.starts')}
                  defaultValue={getScheduleDateValue(formState[ScheduledReportEditorFields.StartDate]?.value)}
                  open
                  hasError={formState[ScheduledReportEditorFields.StartDate]?.error}
                  errorText={translate('common:editors.shared.required')}
                  onChange={function(value) {
                    handleDateChange({
                      name     : ScheduledReportEditorFields.StartDate,
                      value,
                      required : true
                    });
                  }} />
              </FormItem>
              <Render.If condition={isRecurringSchedule}>
                <FormItem halfWidth>
                  <DatePicker key = {endDateKey}
                    useDatePickerTextField
                    manualInput
                    isDatePicker
                    validDateTimeFormats={scheduleDateFormats}
                    allowManualEntry
                    overridePopupStyle={{position: 'relative'}}
                    fieldLabel={translate('sisense:formLabels.ends')}
                    defaultValue={getScheduleDateValue(formState[ScheduledReportEditorFields.EndDate]?.value)}
                    open
                    hasError={formState[ScheduledReportEditorFields.EndDate]?.error}
                    errorText={translate('common:editors.shared.required')}
                    onChange={function(value) {
                      handleDateChange({
                        name     : ScheduledReportEditorFields.EndDate,
                        value,
                        required : false
                      });
                    }} />
                </FormItem>
              </Render.If>
            </FormGroupFields>
          </>
        </FormSection>
      </div>
    </form>
  );
};

export default withSignals(ScheduledReportEditor);
